<template>
  <div class="tw-bg-white tw-rounded-lg tw-p-6 tw-shadow-shadow-2">
    <Typography variant="headline-6"> {{ title }} </Typography>

    <v-form ref="form1" v-model="formValid">
      <Typography variant="subtitle-1" class="tw-mt-3 tw-mb-2">
        Candidate LinkedIn Recruiter URL
      </Typography>
      <div class="tw-flex tw-gap-3">
        <v-text-field
          ref="idealCandidates"
          v-model="importURL"
          outlined
          dense
          required
          hide-details="auto"
          placeholder="https://www.linkedin.com/in/aj-ding-90826856/"
          :rules="[isURL]"
          @focus="removeErorrs"
        >
        </v-text-field>
        <div>
          <Button
            variant="outlined"
            :loading="checkCandidateLoading"
            :disabled="isURL(importURL) === 'Not a valid URL.'"
            @click="checkCandidate"
            >Check Candidate</Button
          >
        </div>
      </div>
      <div v-if="isInProject.display">
        <span v-if="isInProject.error" class="error--text v-messages">
          {{ isInProject.error_message }}
        </span>
        <span v-else class="green--text v-messages">
          {{ isInProject.confirmation_message }}
        </span>
      </div>
      <div v-if="extra">
        <Typography variant="subtitle-1" class="tw-mt-3 tw-mb-2">
          Candidate email
        </Typography>
        <div class="tw-flex tw-gap-3">
          <v-text-field
            v-model="cand_email"
            outlined
            dense
            :rules="emailRules"
            hide-details="auto"
            placeholder="aj@hirefly.ai"
            @focus="emailErrors.error = false"
          >
          </v-text-field>
          <Button
            variant="outlined"
            :loading="checkEmailLoading"
            :disabled="
              rules.linkedinUrl(importURL) === 'Not a valid Linkedin URL' ||
              isInProject.error ||
              cache_url !== importURL
            "
            @click="findEmail"
            >Find Email</Button
          >
        </div>
      </div>
      <div v-if="emailErrors.error">
        <span class="error--text v-messages">
          {{ emailErrors.error_message }}
        </span>
      </div>
      <div v-if="extra && isValidEmail">
        <div>
          <v-radio-group v-model="importType" row>
            <v-radio label="Import as Warm Candidate" value="0"></v-radio>
            <v-radio label="Import to Candidate Queue" value="1"></v-radio>
          </v-radio-group>
        </div>
        <div v-if="importType == 0">
          <Typography variant="subtitle-1" class="tw-mt-3 tw-mb-2">
            Original Email Sender
          </Typography>
          <v-text-field
            v-model="referral_sender"
            outlined
            dense
            required
            hide-details="auto"
            :rules="emailRules"
            class="mt-3"
            placeholder="rnagesh@hirefly-recruiters.com"
          >
          </v-text-field>
          <Typography variant="subtitle-1" class="tw-mt-3 tw-mb-2">
            Referral from Tom
          </Typography>
          <v-text-field
            v-model="referral_subject"
            outlined
            dense
            :rules="[rules.required]"
            hide-details="auto"
            class="mt-3"
            label="Email Subject"
          >
          </v-text-field>
          <Typography variant="subtitle-1" class="tw-mt-3 tw-mb-2">
            Email Body
          </Typography>
          <v-textarea
            v-model="referral_body"
            outlined
            dense
            :rules="[rules.required]"
            hide-details="auto"
            class="mt-3"
            label="Email Body"
          >
          </v-textarea>
        </div>
      </div>
    </v-form>
    <div class="tw-mt-8 tw-flex tw-justify-between">
      <div>
        <Button variant="text" @click="$emit('importDone')">Cancel</Button>
      </div>
      <div>
        <Button
          :loading="importing"
          :disabled="isDisabled"
          @click="importCandidate"
          >{{ extra ? 'Import' : 'Import from LinkedIn' }}</Button
        >
      </div>
    </div>

    <v-snackbar v-model="importSuccessSnackBar" multi-line>
      <div class="font-weight-bold">Successfully imported candidate!</div>

      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="importSuccessSnackBar = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>

    <v-snackbar v-model="importErrorSnackBar" multi-line>
      <div class="font-weight-bold">
        Could not import candidate. Please try again.
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="importErrorSnackBar = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { isURL } from '@/utils';
import { post } from '@/js/api/sourcer';
import consola from 'consola';
import Config from '@/js/config';
import STATUS from '@/store/status';
import Typography from '@/components/v3/base/Typography';
import Button from '@/components/v3/base/Button';

export default {
  components: {
    Typography,
    Button,
  },

  props: {
    projectId: String,
    extra: Boolean,
  },

  data() {
    const isLinkedInURL = (value) => {
      if (!value) return false;
      let str = value;
      if (!str.startsWith('https://') && !str.startsWith('http://')) {
        str = `https://${str}`;
      }

      try {
        const url = new URL(str);
        const profileRegex = /^\/in\/[a-zA-Z0-9]/;
        return (
          (url.protocol === 'http:' || url.protocol === 'https:') &&
          url.hostname === 'www.linkedin.com' &&
          profileRegex.test(url.pathname)
        );
      } catch (error) {
        consola.info(error);
        return false;
      }
    };

    return {
      isURL,
      formValid: false,
      importURL: '',
      cache_url: '',
      cand_email: '',
      referral_sender: '',
      referral_subject: '',
      referral_body: `Hi!

I am a recruiter working on behalf of _______, and has been given your profile from ________. He said you would be a great fit for this position. We are hiring for _________. Can I pass on your information to the hiring manager?`,

      importing: false,
      importSuccessSnackBar: false,
      importErrorSnackBar: false,
      rules: {
        required: (value) => !!value || 'Required.',
        linkedinUrl: (value) =>
          isLinkedInURL(value) || 'Not a valid Linkedin URL',
      },

      isInProject: {
        display: false,
        error: false,
        error_message:
          'Candidate is already in this project and has been moved to Interested. Follow up through the right nav',
        confirmation_message: 'Candidate can be added to this project.',
      },

      emailErrors: {
        error: false,
        cache_url: '',
        error_message: 'Unable to find email for this LinkedIn User.',
      },

      emailRules: [
        (value) => !!value || 'Required.',
        (value) => {
          const pattern =
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
          return pattern.test(value) || 'Invalid e-mail.';
        },
      ],

      importType: '0',
      checkCandidateLoading: false,
      checkEmailLoading: false,
    };
  },

  computed: {
    isValidEmail() {
      return this.emailRules.every((cb) => cb(this.cand_email) === true);
    },

    title() {
      const project = this.getSettingsByProjectId(this.projectId);
      if (project && project.job_title) {
        return `Import a candidate into ${project.project_name}`;
      }
      return 'Import a candidate';
    },

    isDisabled() {
      let disabled = true;
      if (this.formValid) disabled = false;
      return disabled;
    },

    ...mapGetters('projects/settings', ['getSettingsByProjectId']),

    isV2() {
      return this.$store.getters['user/getIsUserV2'];
    },

    currentProject() {
      return (
        this.$store.getters['projects/getProjectById'](this.projectId) || {}
      );
    },
  },

  created() {
    this.$store.dispatch('projects/settings/loadSettings', {
      projectId: this.projectId,
    });
  },

  methods: {
    async importCandidate() {
      try {
        this.$amplitude_client.logEvent({
          event_type: 'import_candidate_fe',
          user_id: localStorage.getItem('email'),
          event_properties: {
            project_id: this.projectId,
            cand_linkedin_url: this.importURL,
          },
        });

        this.importing = true;
        // TODO(jeremiah): clean this up after v2 rollout
        let importObj;
        if (this.isV2) {
          await this.importCandidateV2();
        } else {
          await this.importCandidateV1();
        }
        this.importing = false;
      } catch (error) {
        consola.info("Couldn't import candidate");
        this.importing = false;
        this.importErrorSnackBar = true;
      }
    },

    async importCandidateV2() {
      const payload = {
        project_id: this.projectId,
        candidate_linkedin_url: this.importURL,
        autopilot_import_to_queue:
          this.importType === 1 || this.importType === '1',
      };
      let tabTarget = 'APPROVED';
      if (this.currentProject.type === '1') {
        tabTarget =
          this.importType === 1 || this.importType === '1'
            ? 'AWAITING_INFO'
            : 'HIRING_MANAGER';
      }
      const response = await post(
        `${Config.env.API_URL_V2}projects/candidates/import`,
        payload
      );

      if (response && response.status === 209) {
        this.isInProject.error = true;
        this.isInProject.display = true;
      } else if (response && response.status === 404) {
        this.emailErrors.error = true;
      } else if (response && response.status === 200) {
        this.updateStoreWithImportedCandidate(tabTarget);
      }
    },

    async importCandidateV1() {
      const payload = {
        project_id: this.projectId,
        cand_linkedin_url: this.importURL,
        status: 'APPROVED',
      };
      let tabTarget = 'APPROVED';
      if (this.extra) {
        payload.cand_email = this.cand_email;
        tabTarget = 'WARM_LEADS_AWAITING_INFO';
        if (this.importType === 0 || this.importType === '0') {
          payload.status = 'WARM_LEADS_AWAITING_INFO';
          payload.referral_sender = this.referral_sender;
          payload.referral_subject = this.referral_subject;
          payload.referral_body = this.referral_body;
        } else {
          payload.status = 'UNDER_REVIEW';
        }
      }
      const response = await post(`${Config.env.API_URL}candidates`, payload);

      if (response && response.status === 200) {
        this.isInProject.error = true;
        this.isInProject.display = true;
      } else if (response && response.status === 201) {
        this.updateStoreWithImportedCandidate(tabTarget);
      }
    },

    updateStoreWithImportedCandidate(tabTarget) {
      consola.info('Successfully imported candidate');
      this.$store.commit('projects/candidates/SET_STATUS', {
        projectId: this.projectId,
        tab: tabTarget,
        status: STATUS.STALE,
      });
      this.$store.commit('projects/SET_STATUS', {
        status: STATUS.STALE,
      });
      this.$store.dispatch('projects/candidates/loadCandidates', {
        projectId: this.projectId,
        tab: tabTarget,
      });
      this.$store.dispatch('projects/importCadidateCount', {
        projectId: this.projectId,
        tab: tabTarget,
      });
      consola.info('Updated vuex');
      this.$emit('importDone');
    },

    async findCandidateInfo() {
      this.emailErrors.error = false;
      const payload = {
        project_id: this.projectId,
        cand_linkedin_url: this.importURL,
      };
      try {
        const response = await post(
          `${Config.env.API_URL}candidates/import/check`,
          payload
        );
        this.isInProject.error = response.data.in_project;
        if (response.data.email) this.last_email = response.data.email;
        else {
          this.emailErrors.cache_url = this.importURL;
        }
      } catch (error) {
        consola.info("Couldn't confirm candidate was in project.");
      }
      this.cache_url = this.importURL;
    },

    async checkCandidate() {
      this.checkCandidateLoading = true;
      if (this.cache_url !== this.importURL) await this.findCandidateInfo();
      this.isInProject.display = true;
      this.checkCandidateLoading = false;
    },

    async findEmail() {
      this.checkEmailLoading = true;
      if (this.cache_url !== this.importURL) await this.findCandidateInfo();
      if (this.cache_url !== this.emailErrors.cache_url) {
        this.cand_email = this.last_email;
      } else {
        this.emailErrors.error = true;
      }
      this.checkEmailLoading = false;
    },

    removeErorrs() {
      this.emailErrors.error = false;
      this.isInProject.display = false;
    },
  },
};
</script>

<style scoped lang="scss">
.form-section ::v-deep .v-input input,
.form-section ::v-deep .v-input,
.form-section ::v-deep .v-label {
  font-size: 14px;
}
::v-deep {
  .v-input__slot {
    min-height: 44px !important;
  }
}
</style>
